/**
 * Atribuição de origem do contato no painel: tracking, enrich Meta e sync no lead.
 */
import { useEffect, useRef, useState } from 'react';
import type { Contact, Lead } from '../../../types';
import type { MetaAttribution } from './types';
import {
    ContactTrackingAttribution,
    enrichMetaAttribution,
    getContactTrackingAttribution,
    metaDisplayName,
    resolveMetaOriginLabel,
} from '../../../services/modules/leadTrackingService';
import {
    preferLockedOriginLabel,
    resolveAdsOriginKind,
    type AdsOriginKind,
} from '../lib/adsOriginPanel';

const ORIGIN_LABEL: Partial<Record<AdsOriginKind, string>> = { meta: 'Meta Ads', google: 'Google Ads' };

type OriginNames = { campaign?: string; adSet?: string; ad?: string };

/** Resolve campanha/conjunto/anúncio a partir das fontes (prioridade = ordem no array). */
function resolveOriginNames(
    meta: MetaAttribution | undefined,
    lead: Lead | null | undefined,
    tracking: ContactTrackingAttribution | null,
    enriched: ContactTrackingAttribution | null,
): OriginNames {
    const d = lead?.originDetails;
    return {
        campaign: resolveMetaOriginLabel([meta?.campaignName, d?.campaign, tracking?.campaignName, enriched?.campaignName]),
        adSet: resolveMetaOriginLabel([meta?.adsetName, d?.adSet, tracking?.adsetName, enriched?.adsetName]),
        ad: resolveMetaOriginLabel([meta?.adName, meta?.headline, d?.ad, tracking?.adName, enriched?.adName]),
    };
}

interface UseContactOriginAttributionParams {
    isOpen: boolean;
    currentLead: Lead | null | undefined;
    metaAttribution?: MetaAttribution;
    remoteJid?: string;
    currentContact?: Contact | null;
    organizationId?: string;
    onLeadUpdate?: (lead: Lead) => Promise<void>;
}

export function useContactOriginAttribution({
    isOpen,
    currentLead,
    metaAttribution,
    remoteJid,
    currentContact,
    organizationId,
    onLeadUpdate,
}: UseContactOriginAttributionParams) {
    const [tracking, setTracking] = useState<ContactTrackingAttribution | null>(null);
    const [enriched, setEnriched] = useState<ContactTrackingAttribution | null>(null);
    const syncedKeyRef = useRef<string | null>(null);
    const enrichAttemptedRef = useRef<string | null>(null);

    const contactId = currentContact?.id;
    // Select editável = só contato. Lead/meta entram só para detectar anúncio pago (trava).
    const contactOriginLabel = currentContact?.origins?.name?.trim() || undefined;
    const adsSignalLabel = preferLockedOriginLabel(
        contactOriginLabel,
        currentLead?.origin,
        metaAttribution?.origin,
    );
    const kind = resolveAdsOriginKind({
        originLabel: adsSignalLabel,
        source: tracking?.source,
        ctwaClid: metaAttribution?.ctwaClid,
        googleClickId: metaAttribution?.googleClickId ?? tracking?.googleClickId,
        sourceId: metaAttribution?.sourceId,
        adId: metaAttribution?.adId,
    });
    const isFromAds = kind === 'meta' || kind === 'google';
    const adsKind = isFromAds ? kind : null;
    const originLabel = isFromAds
        ? (ORIGIN_LABEL[kind] ?? adsSignalLabel ?? 'Não informado')
        : (contactOriginLabel || 'Não informado');

    const { campaign, adSet, ad } = resolveOriginNames(metaAttribution, currentLead, tracking, enriched);
    const keyword = metaAttribution?.keyword || tracking?.keyword;

    useEffect(() => {
        if (!isOpen || !contactId || !organizationId) {
            setTracking(null);
            setEnriched(null);
            enrichAttemptedRef.current = null;
            return;
        }
        let cancelled = false;
        void getContactTrackingAttribution(organizationId, contactId).then((data) => {
            if (!cancelled) setTracking(data);
        });
        return () => { cancelled = true; };
    }, [isOpen, contactId, organizationId]);

    useEffect(() => {
        if (!isOpen || kind !== 'meta' || !organizationId || !remoteJid) {
            setEnriched(null);
            return;
        }

        // Enrich enquanto faltar hierarquia Graph (campanha+conjunto+anúncio real).
        // CTA sozinho em `ad` (ex.: “📲 Clique aqui…”) NÃO deve bloquear o enrich.
        const base = resolveOriginNames(metaAttribution, currentLead, tracking, null);
        if (base.campaign && base.adSet && base.ad) {
            setEnriched(null);
            return;
        }

        const hintAdId = tracking?.adId || metaAttribution?.sourceId || metaAttribution?.adId;
        const attemptKey = `${organizationId}:${remoteJid}:${contactId ?? ''}:${hintAdId ?? ''}`;
        if (enrichAttemptedRef.current === attemptKey) return;
        enrichAttemptedRef.current = attemptKey;

        let cancelled = false;
        void enrichMetaAttribution(organizationId, remoteJid, contactId, hintAdId).then((data) => {
            if (!cancelled && data) setEnriched(data);
        });
        return () => { cancelled = true; };
    }, [isOpen, kind, organizationId, remoteJid, contactId, metaAttribution, currentLead, tracking]);

    useEffect(() => {
        if (!onLeadUpdate || !currentLead || !isFromAds) return;
        if (!campaign && !adSet && !ad) return;

        const syncKey = `${currentLead.id}:${campaign || ''}:${adSet || ''}:${ad || ''}`;
        if (syncedKeyRef.current === syncKey) return;
        syncedKeyRef.current = syncKey;

        const cur = currentLead.originDetails || {};
        const nextCampaign = metaDisplayName(cur.campaign) || campaign;
        const nextAdSet = metaDisplayName(cur.adSet) || adSet;
        const nextAd = metaDisplayName(cur.ad) || ad;
        const needsSync =
            (campaign && !metaDisplayName(cur.campaign)) ||
            (adSet && !metaDisplayName(cur.adSet)) ||
            (ad && !metaDisplayName(cur.ad));
        if (!needsSync) return;

        void onLeadUpdate({
            ...currentLead,
            originDetails: { campaign: nextCampaign, adSet: nextAdSet, ad: nextAd },
        });
    }, [onLeadUpdate, currentLead, isFromAds, campaign, adSet, ad]);

    return {
        kind,
        adsKind,
        isFromAds,
        originLabel,
        contactOriginLabel,
        campaign,
        adSet,
        ad,
        keyword,
    };
}
